<template>
    <bottom-sheet dismiss-label="Close">
        <template #top>
            <bubble-chart
                v-if="showChart"
                :chart-data="chartData"
                :styles="{ position: 'relative', width: '100%', height: '200px' }"
                class="mb-3"
            />
        </template>

        <template #title>
            <span>{{ currentAsset.symbol }}</span>
            <span class="sheet-title-sub">{{ windowLabel }}</span>
            <b-link
                href="#"
                class="ml-auto pin-star"
                :class="anchorEvent.pinned ? 'text-warning' : 'text-secondary'"
                @click.prevent="requestPinChange"
            >
                <b-icon :icon="starIcon"></b-icon>
            </b-link>
        </template>

        <confirm-prompt
            :visible="unpinConfirmVisible"
            title="Unpin event"
            :message="confirmMessage"
            :note="confirmNote"
            confirm-label="Unpin"
            confirm-variant="btn-warning"
            @confirm="togglePin"
            @cancel="cancelUnpin"
        />

        <div class="sheet-section-label">Event</div>
        <record-row strip>
            <div class="anchor-type" :style="{ color: anchorTypeColor }">{{ anchorType }}</div>
            <div class="mono">{{ eventSize(anchorEvent) ? formatAmount(eventSize(anchorEvent)) : '' }}</div>
            <div class="record-date">{{ formatTimeFromISO(anchorEvent.timestamp) }}</div>
        </record-row>

        <div v-if="loading" class="sheet-muted-row">
            <b-spinner small variant="light"></b-spinner>
        </div>

        <div v-else-if="!aggregates" class="sheet-muted-row">
            Could not read what happened since this event
        </div>

        <div v-else-if="!aggregates.event_count" class="sheet-muted-row">
            Nothing happened since this event
        </div>

        <sheet-section v-else label="Since then">
            <div v-for="row in summaryRows" :key="row.label" class="insight-row">
                <span>
                    {{ row.label }}
                    <span v-if="row.count !== null" class="insight-count">({{ row.count }})</span>
                </span>
                <span class="mono" :class="row.valueClass">{{ row.value }}</span>
                <span class="insight-average mono">
                    <template v-if="row.average">AVG {{ formatPrice(row.average) }}</template>
                </span>
            </div>
        </sheet-section>
    </bottom-sheet>
</template>

<script>
import apiMixin from '@/mixins/api';
import formatter from '@/mixins/formatter';
import eventsMixin from '@/mixins/eventsMixin';

import BubbleChart from '@/components/charts/BubbleChart.vue';
import ConfirmPrompt from '@/components/overlays/ConfirmPrompt.vue';
import BottomSheet from '@/components/sheet/BottomSheet.vue';
import SheetSection from '@/components/sheet/SheetSection.vue';
import RecordRow from '@/components/sheet/RecordRow.vue';

import {
    CHART_BUBBLE_BASE_RADIUS,
    CHART_COLOR_BUY,
    CHART_COLOR_LARGE_BUY,
    CHART_COLOR_PRICE_LINE,
    CHART_COLOR_SELL,
    CHART_MIN_EVENT_SPAN_DAYS,
} from '@/constants/ui';

const DAY_MS = 86400000;

export default {
    name: 'EventInsights',
    props: ['anchorEvent', 'eventList', 'currentAsset', 'chartDays', 'onPinned'],
    mixins: [formatter, eventsMixin, apiMixin],
    components: { BubbleChart, ConfirmPrompt, BottomSheet, SheetSection, RecordRow },
    data() {
        return {
            aggregates: null,
            unpinConfirmVisible: false,
            eventTypeLabels: {
                buy: 'buy',
                exit: 'exit',
                bonus_exit: 'bonus',
                reinvest: 'reinvest',
                conviction: 'conviction',
            },
        };
    },
    mounted() {
        this.loadAggregates();
    },
    methods: {
        // the API owns these numbers: the page holds only the event window, and a
        // pinned anchor can sit outside it
        async loadAggregates() {
            const response = await this.apiGet(
                `/api/event/${this.anchorEvent.public_id}/aggregates`,
                {},
                { requestId: 'anchorAggregates' }
            );
            if (!response) return;

            this.aggregates = response.data.aggregates;
        },
        // a pin is one tap; an unpin can take the row off the page, so it asks first
        requestPinChange() {
            if (this.anchorEvent.pinned) {
                this.unpinConfirmVisible = true;
                return;
            }

            return this.togglePin();
        },
        cancelUnpin() {
            this.unpinConfirmVisible = false;
        },
        async togglePin() {
            this.unpinConfirmVisible = false;

            const response = await this.apiPatch(
                `/api/event/${this.anchorEvent.public_id}`,
                { pinned: !this.anchorEvent.pinned }
            );
            if (!response) return;

            // the page owns the row objects; it applies the flag and the list re-sorts
            this.onPinned(response.data.event.pinned);
        },
        // days back from the end of today, the x the API assigns to the asset page chart
        chartDaysBack(timestamp) {
            const midnight = new Date(`${timestamp.split('T')[0]}T00:00:00Z`);
            const today = new Date();
            today.setUTCHours(0, 0, 0, 0);

            const fractionOfDay = (this.dateFromISO(timestamp) - midnight) / DAY_MS;
            const daysAgo = Math.round((today - midnight) / DAY_MS);

            return fractionOfDay - daysAgo - 1;
        },
        bubbleRadius(positions) {
            return CHART_BUBBLE_BASE_RADIUS * Math.sqrt(positions || 1);
        },
    },
    computed: {
        loading() {
            return !!this.apiLoading['anchorAggregates'];
        },
        starIcon() {
            return this.anchorEvent.pinned ? 'star-fill' : 'star';
        },
        confirmMessage() {
            return `Unpin the ${this.anchorLabel}?`;
        },
        confirmNote() {
            if (this.anchorInWindow) return '';

            return 'THIS EVENT IS OUTSIDE THE DISPLAY WINDOW';
        },
        // eventList is the window the page holds, so an absent anchor is older than it
        anchorInWindow() {
            return this.eventList.some(event => event.public_id === this.anchorEvent.public_id);
        },
        elapsedMs() {
            return new Date() - this.dateFromISO(this.anchorEvent.timestamp);
        },
        daysSince() {
            return Math.floor(this.elapsedMs / DAY_MS);
        },
        anchorType() {
            if (this.anchorEvent.subtype === 'exit') return 'SELL';

            const label = this.eventTypeLabels[this.anchorEvent.subtype];
            return label ? label.toUpperCase() : 'EVENT';
        },
        anchorTypeColor() {
            if (this.anchorEvent.subtype === 'buy') return CHART_COLOR_BUY;
            if (this.anchorEvent.subtype === 'exit') return CHART_COLOR_SELL;

            return null;
        },
        windowLabel() {
            if (this.daysSince) return `last ${this.daysSince} day${this.daysSince === 1 ? '' : 's'}`;

            const hours = Math.max(1, Math.floor(this.elapsedMs / 3600000));

            return `last ${hours} hour${hours === 1 ? '' : 's'}`;
        },
        anchorLabel() {
            const date = this.dateFromISO(this.anchorEvent.timestamp);
            const day = `${this.monthName[date.getUTCMonth()]} ${date.getUTCDate()}`;
            const type = this.eventTypeLabels[this.anchorEvent.subtype] || 'event';
            const size = this.eventSize(this.anchorEvent);
            const price = this.eventPrice(this.anchorEvent);

            const amount = size ? `${this.formatAmount(size)} ` : '';
            const at = price ? ` at $${this.formatPrice(price)}` : '';

            return `${amount}${type} on ${day}${at}`;
        },
        netFormatted() {
            const net = this.aggregates ? this.aggregates.net : 0;
            const sign = net > 0 ? '+' : net < 0 ? '-' : '';
            return `${sign}${this.formatAmount(Math.abs(net))}`;
        },
        netClass() {
            const net = this.aggregates ? this.aggregates.net : 0;
            if (net > 0) return 'text-success';
            if (net < 0) return 'text-danger';
            return '';
        },
        summaryRows() {
            if (!this.aggregates) return [];

            return [
                {
                    label: 'exits',
                    count: this.aggregates.exit_events,
                    value: this.formatAmount(this.aggregates.exits),
                    average: this.aggregates.exit_price,
                    valueClass: '',
                },
                {
                    label: 'buys',
                    count: this.aggregates.buy_events,
                    value: this.formatAmount(this.aggregates.buys),
                    average: this.aggregates.buy_price,
                    valueClass: '',
                },
                {
                    label: 'net',
                    count: null,
                    value: this.netFormatted,
                    average: 0,
                    valueClass: this.netClass,
                },
            ];
        },
        eventPoints() {
            const points = { buys: [], largeBuys: [], exits: [] };

            this.eventsSince(this.eventList, this.anchorEvent).forEach(event => {
                const data = event.data || {};
                const x = this.chartDaysBack(event.timestamp);

                if (event.subtype === 'buy') {
                    const buy = { x, y: data.buy_price, r: this.bubbleRadius(data.new_pids?.length) };

                    if (data.multiplier > 1) points.largeBuys.push(buy);
                    else points.buys.push(buy);
                } else if (event.subtype === 'exit') {
                    points.exits.push({ x, y: data.target_price, r: this.bubbleRadius(data.exit_pids?.length) });
                }
            });

            return points;
        },
        chartedDaySpan() {
            const days = [
                ...this.eventPoints.buys,
                ...this.eventPoints.largeBuys,
                ...this.eventPoints.exits,
            ].map(point => point.x);

            if (!days.length) return 0;

            return Math.max(...days) - Math.min(...days);
        },
        // an older anchor's bubbles would fall off the left edge of the x axis
        anchorInChartWindow() {
            return this.chartDaysBack(this.anchorEvent.timestamp) >= -this.chartDays;
        },
        showChart() {
            return this.anchorInChartWindow && this.chartedDaySpan >= CHART_MIN_EVENT_SPAN_DAYS;
        },
        chartData() {
            // the flat price line holds the x axis open over the same window the asset page charts
            const priceLine = [];
            for (let day = -this.chartDays; day <= 0; day++) {
                priceLine.push({ x: day, y: this.currentAsset.price, r: 0 });
            }

            const datasets = [{
                label: 'Current price',
                type: 'line',
                backgroundColor: CHART_COLOR_PRICE_LINE,
                data: priceLine,
                pointStyle: 'dash',
            }];

            if (this.eventPoints.buys.length) datasets.push({
                label: 'Buys',
                type: 'bubble',
                backgroundColor: CHART_COLOR_BUY,
                data: this.eventPoints.buys,
            });

            if (this.eventPoints.largeBuys.length) datasets.push({
                label: 'Large Buys',
                type: 'bubble',
                backgroundColor: CHART_COLOR_LARGE_BUY,
                data: this.eventPoints.largeBuys,
                pointStyle: 'rectRot',
            });

            if (this.eventPoints.exits.length) datasets.push({
                label: 'Exits',
                type: 'bubble',
                backgroundColor: CHART_COLOR_SELL,
                data: this.eventPoints.exits,
            });

            return { datasets };
        },
    },
};
</script>

<style scoped>
/* the icon sizes off the font, and the title makes it far bigger than the row needs */
.pin-star {
    font-size: 18px;
}
/* a long type such as CONVICTION has no amount, so it may run into the empty amount slot */
.anchor-type {
    overflow: visible;
    color: var(--sheet-muted);
    font-weight: 700;
    letter-spacing: .05em;
}
.insight-row {
    display: grid;
    grid-template-columns: 1.1fr 1fr 1fr;
    align-items: baseline;
    padding: 10px 12px;
    border-top: 1px solid var(--sheet-divider);
}
.insight-row:first-child {
    border-top: 0;
}
.insight-count {
    color: var(--sheet-muted);
    font-size: 12px;
}
.insight-average {
    text-align: right;
    color: var(--sheet-muted);
    font-size: 12.5px;
}
</style>
